import { useMemo } from "react";
import type { ReactNode } from "react";

// External Libraries
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Menubar } from "primereact/menubar";
import { Button } from "primereact/button";
import type { MenuItem } from "primereact/menuitem";

// Components
import { ThemeLogo } from "sj-ui-toolkit/src/theme/components/ThemeLogo";

// Hooks
import { useChatIntegration } from "sj-ui-toolkit/src/integrations";

// Styles
import S from "./LoginLayout.module.scss";

// Constants
import { HOME_LINK, KNOWLEDGE_BASE_LINK, PRIVACY_LINK, TERMS_LINK } from "../../../constants/links";

interface LoginLayoutProps {
  title: string;
  children: ReactNode;
  titleClassName?: string;
  noFooter?: boolean;
  cardClassName?: string;
}

export function LoginLayout({
  title,
  titleClassName = "p-card-title my-3",
  children,
  noFooter = false,
  cardClassName = "max-w-md",
}: LoginLayoutProps) {
  useChatIntegration({ enableAnonymousChat: true });

  /**
   * Prime React Menubar intercepts keyboard events, preventing default link behavior.
   * This handler restores accessibility by manually triggering the click event
   */
  const handleLinkKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const itemRenderer = (item: MenuItem) => (
    <a
      href={item.url}
      className={S.menuItem}
      target="_blank"
      rel="noopener noreferrer"
      onKeyDown={handleLinkKeyDown}
    >
      {item.label}
    </a>
  );

  const items = useMemo(
    () => [
      {
        label: "Home",
        url: HOME_LINK,
        template: itemRenderer,
      },
      {
        label: "Knowledge Base",
        url: KNOWLEDGE_BASE_LINK,
        template: itemRenderer,
      },
    ],
    []
  );

  const StartTemplate = () => {
    return <ThemeLogo />;
  };

  const cardTitle = (
    <h1 className={titleClassName} id="page-title">
      {title}
    </h1>
  );

  return (
    <div className="min-h-screen flex flex-col atlas-bg-home">
      <header>
        <Menubar
          aria-label="Login page navigation"
          start={StartTemplate}
          model={items}
          className="w-full"
          pt={{
            root: { className: S.header },
            menu: { className: "ml-auto" },
            submenu: { role: "none" },
            button: { style: { backgroundColor: "transparent", color: "#fff" } },
            icon: { style: { color: "#fff" } },
          }}
        />
      </header>
      <main className="flex flex-1 justify-center items-center" aria-labelledby="page-title">
        <Card title={cardTitle} className={`w-full ${cardClassName}`}>
          {children}

          {!noFooter && (
            <>
              <Divider />
              <div className={S.footerContainer}>
                <div className={S.footer}>
                  <Button
                    className={S.termsPolicyButton}
                    link
                    onClick={() => window.open(TERMS_LINK, "_blank")}
                    label="Terms of Use"
                  />
                  and
                  <Button
                    className={S.termsPolicyButton}
                    onClick={() => window.open(PRIVACY_LINK, "_blank")}
                    link
                    label="Privacy Policy"
                  />
                </div>
              </div>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
